import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { buttonVariants } from "@/components/ui/button";
import { useDeleteSolutions, useDeleteSolutionsPreview } from "@/hooks/use-delete-solutions";
import { cn } from "@/lib/utils";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ids: string[];
  onDeleted?: () => void;
}

export function DeleteSolutionsDialog({ open, onOpenChange, ids, onDeleted }: Props) {
  const preview = useDeleteSolutionsPreview(ids, open);
  const del = useDeleteSolutions();

  const confirm = () => {
    del.mutate(ids, {
      onSuccess: ({ results }) => {
        const failed = results.filter((r) => !r.ok);
        const okCount = results.length - failed.length;
        if (failed.length === 0) {
          toast.success(`Deleted ${okCount} Solution${okCount === 1 ? "" : "s"}`);
        } else {
          toast.error(`Deleted ${okCount}; ${failed.length} failed`);
        }
        onOpenChange(false);
        onDeleted?.();
      },
      onError: (err) => toast.error(err.message),
    });
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-[var(--fit-at-risk)]">
            <AlertTriangle className="h-5 w-5" />
            Delete {ids.length} Solution{ids.length === 1 ? "" : "s"} permanently?
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p className="font-medium text-foreground">
                This is destructive and cannot be undone. The following will be permanently removed:
              </p>
              {preview.isLoading && <p className="text-muted-foreground">Loading impact…</p>}
              {preview.isError && (
                <p className="text-[var(--fit-at-risk)]">
                  Could not load impact. You can still proceed.
                </p>
              )}
              <ul className="max-h-64 space-y-2 overflow-y-auto rounded-md border border-border p-2">
                {(preview.data ?? []).map((p) => (
                  <li key={p.id} className="text-sm">
                    <span className="font-medium text-foreground">{p.name}</span>
                    <span className="text-muted-foreground"> · {p.accountName}</span>
                    {(p.assignments > 0 || p.pods > 0 || p.snapshots > 0) && (
                      <div className="text-[12px] text-muted-foreground">
                        also removes {p.assignments} assignment{p.assignments === 1 ? "" : "s"},{" "}
                        {p.pods} Pod{p.pods === 1 ? "" : "s"}, {p.snapshots} Fit snapshot
                        {p.snapshots === 1 ? "" : "s"}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={del.isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className={cn(buttonVariants({ variant: "destructive" }))}
            disabled={del.isPending}
            onClick={(e) => {
              e.preventDefault();
              confirm();
            }}
          >
            {del.isPending ? "Deleting…" : "Delete permanently"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
